import { getDrofusDateRange, readDrofusDateRange, saveDrofusDateRange, type DrofusDatePreset } from "./drofusDateRange";
import type { DrofusForekomstDetailsRef as IOccurrenceDetailsRef } from "../DrofusForekomster/DrofusForekomstDetailsModal";
import type {
  IDrofusInterfaceChangeSeverity,
  IDrofusInterfaceChangeType,
  IDrofusInterfaceDiscipline,
} from "@multiconsult-group/multiplacer-core";
import { createAppSlice } from "../app/createAppSlice";

export type IDrofusEndringerViewMode = "changes" | "rooms";

export interface IDrofusEndringerSort {
  field:
    | "runStartedAt"
    | "createdAt"
    | "changeType"
    | "severity"
    | "roomFunctionNo"
    | "roomName"
    | "compCode"
    | "articleName"
    | "field";
  direction: "asc" | "desc";
}

export type IDrofusEndringerOccurrenceRef = IOccurrenceDetailsRef;

export interface IDrofusEndringerState {
  viewMode: IDrofusEndringerViewMode;
  includeSynchronizationChanges: boolean;
  search: string;
  datePreset: DrofusDatePreset;
  dateFrom: string;
  dateTo: string;
  changeTypes: IDrofusInterfaceChangeType[];
  affectedDisciplines: IDrofusInterfaceDiscipline[];
  mmiCodes: string[];
  attributeKeys: string[];
  severities: IDrofusInterfaceChangeSeverity[];
  limit: number;
  offset: number;
  roomChangesPage: number;
  sort: IDrofusEndringerSort;
  selectedOccurrenceRef: IDrofusEndringerOccurrenceRef | null;
}

export const DROFUS_ENDRINGER_DISCIPLINE_OPTIONS: Array<{
  value: IDrofusInterfaceDiscipline;
  label: string;
  color: string;
}> = [
  { value: "RIE", label: "RIE", color: "#f59f00" },
  { value: "RIKT", label: "RIKT", color: "#1971c2" },
  { value: "RIAut", label: "RIAUT", color: "#9c36b5" },
  { value: "RIV", label: "RIV", color: "#0c8599" },
];

const getDefaultDrofusEndringerState = (): IDrofusEndringerState => {
  return {
    viewMode: "changes",
    includeSynchronizationChanges: false,
    search: "",
    ...readDrofusDateRange(),
    changeTypes: [],
    affectedDisciplines: [],
    mmiCodes: [],
    attributeKeys: [],
    severities: [],
    limit: 100,
    offset: 0,
    roomChangesPage: 1,
    sort: {
      field: "runStartedAt",
      direction: "desc",
    },
    selectedOccurrenceRef: null,
  };
};

export const drofusEndringerSlice = createAppSlice({
  name: "drofusEndringer",
  initialState: getDefaultDrofusEndringerState,
  reducers: (create) => ({
    initializeDrofusEndringerDateRange: create.preparedReducer(
      () => ({ payload: readDrofusDateRange() }),
      (state, action) => {
        Object.assign(state, action.payload);
        state.offset = 0;
        state.roomChangesPage = 1;
      }
    ),
    setDrofusEndringerDatePreset: create.preparedReducer(
      (preset: DrofusDatePreset) => ({ payload: preset === "custom" ? { datePreset: preset } : getDrofusDateRange(preset) }),
      (state, action) => {
        Object.assign(state, action.payload);
        saveDrofusDateRange(state);
        state.offset = 0;
        state.roomChangesPage = 1;
      }
    ),
    setDrofusEndringerIncludeSynchronizationChanges: create.reducer<boolean>((state, action) => {
      state.includeSynchronizationChanges = action.payload;
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerSearch: create.reducer<string>((state, action) => {
      state.search = action.payload;
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerViewMode: create.reducer<IDrofusEndringerViewMode>((state, action) => {
      state.viewMode = action.payload;
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerDateFrom: create.reducer<string>((state, action) => {
      state.dateFrom = action.payload;
      state.datePreset = "custom";
      saveDrofusDateRange(state);
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerDateTo: create.reducer<string>((state, action) => {
      state.dateTo = action.payload;
      state.datePreset = "custom";
      saveDrofusDateRange(state);
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    toggleDrofusEndringerChangeType: create.reducer<IDrofusInterfaceChangeType>((state, action) => {
      state.changeTypes = state.changeTypes.includes(action.payload)
        ? state.changeTypes.filter((item) => item !== action.payload)
        : [...state.changeTypes, action.payload];
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    toggleDrofusEndringerDiscipline: create.reducer<IDrofusInterfaceDiscipline>((state, action) => {
      state.affectedDisciplines = state.affectedDisciplines.includes(action.payload)
        ? state.affectedDisciplines.filter((item) => item !== action.payload)
        : [...state.affectedDisciplines, action.payload];
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    toggleDrofusEndringerMmi: create.reducer<string>((state, action) => {
      state.mmiCodes = state.mmiCodes.includes(action.payload)
        ? state.mmiCodes.filter((item) => item !== action.payload)
        : [...state.mmiCodes, action.payload];
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerAttributeKeys: create.reducer<string[]>((state, action) => {
      state.attributeKeys = [...new Set(action.payload)];
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerLimit: create.reducer<number>((state, action) => {
      state.limit = action.payload;
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    setDrofusEndringerRoomChangesPage: create.reducer<number>((state, action) => {
      state.roomChangesPage = Number.isFinite(action.payload)
        ? Math.max(1, Math.floor(action.payload))
        : 1;
    }),
    setDrofusEndringerOffset: create.reducer<number>((state, action) => {
      state.offset = action.payload;
    }),
    setDrofusEndringerSort: create.reducer<IDrofusEndringerSort>((state, action) => {
      state.sort = action.payload;
      state.offset = 0;
      state.roomChangesPage = 1;
    }),
    openDrofusEndringerOccurrenceDetails: create.reducer<IDrofusEndringerOccurrenceRef>(
      (state, action) => {
        state.selectedOccurrenceRef = action.payload;
      }
    ),
    closeDrofusEndringerOccurrenceDetails: create.reducer((state) => {
      state.selectedOccurrenceRef = null;
    }),
    resetDrofusEndringerFilters: create.preparedReducer(
      () => ({ payload: { ...getDefaultDrofusEndringerState(), ...getDrofusDateRange("week") } }),
      (state, action) => {
        const selectedOccurrenceRef = state.selectedOccurrenceRef;
        Object.assign(state, action.payload);
        saveDrofusDateRange(state);
        state.selectedOccurrenceRef = selectedOccurrenceRef;
      }
    ),
  }),
  extraReducers: (builder) => {
    // Match cross-slice actions without importing the design slice's store dependencies.
    builder.addMatcher(
      (action) =>
        action.type === "design/setSelectedSpaceNumberInRedux" ||
        action.type === "design/setFloorAndJobInRedux" ||
        action.type === "global/setActiveProjectTabInRedux" ||
        action.type === "design/setProjectNoInRedux" ||
        action.type.startsWith("pnsSelection/"),
      (state) => {
        state.roomChangesPage = 1;
      }
    );
  },
});

export const {
  initializeDrofusEndringerDateRange,
  setDrofusEndringerDatePreset,
  setDrofusEndringerIncludeSynchronizationChanges,
  setDrofusEndringerSearch,
  setDrofusEndringerViewMode,
  setDrofusEndringerDateFrom,
  setDrofusEndringerDateTo,
  toggleDrofusEndringerChangeType,
  toggleDrofusEndringerDiscipline,
  toggleDrofusEndringerMmi,
  setDrofusEndringerAttributeKeys,
  setDrofusEndringerLimit,
  setDrofusEndringerOffset,
  setDrofusEndringerRoomChangesPage,
  setDrofusEndringerSort,
  openDrofusEndringerOccurrenceDetails,
  closeDrofusEndringerOccurrenceDetails,
  resetDrofusEndringerFilters,
} = drofusEndringerSlice.actions;
